// auditpm-v6
import React, { useEffect, useState } from 'react'
import { useAuth } from '../lib/AuthContext'
import { getTemplate, applyTemplate } from './Settings'
import { useNavigate } from 'react-router-dom'
import { supabase, AUDIT_TYPES } from '../lib/supabase'
import { isOtherService, OTE_TYPE } from '../lib/service'
import { oteQuestionnaireEmail } from '../lib/oteQuote'
import { toast, failed } from '../lib/toast'
import { derivePipeline, expiringRenewals } from '../lib/pipeline'
import { quotesToPrepare, quotesAwaitingSignature } from '../lib/quotationStatus'
import { unresolvedHighRiskCoiNames } from '../lib/coi'
import { displayState } from '../lib/workflow'
import { deriveDashboard } from '../lib/dashboardAttention'
import { DASHBOARD_JOB_COLUMNS } from '../lib/jobLists'

// (STATUS_BADGE map removed with the Recent-activity list — no longer rendered on the dashboard.)

function SendEnquiryModal({ onClose }) {
  const { profile } = useAuth()
  const [auditType, setAuditType] = useState(AUDIT_TYPES.BIZSAFE)
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [sending, setSending] = useState(false)
  const [done, setDone] = useState(false)
  const [link, setLink] = useState('')

  async function send() {
    setSending(true)
    // OTE has its own questionnaire (as Quotations → OTE → "Send OTE questionnaire").
    if (auditType === OTE_TYPE) {
      if (!/^\S+@\S+\.\S+$/.test(email.trim())) { setSending(false); toast('Enter the client’s email', 'warning'); return }
      const token = window.crypto?.randomUUID ? window.crypto.randomUUID().replace(/-/g, '') : Math.random().toString(36).slice(2) + Date.now().toString(36)
      const { data: me } = await supabase.auth.getUser()
      const { error } = await supabase.from('ote_form_links').insert({ token, client_id: null, sent_to: email.trim(),
        initials: profile?.initials || null, created_by: me?.user?.email || null })
      if (error) { setSending(false); failed("Couldn't create the questionnaire link", error.message); return }
      const url = `${window.location.origin}/ote-form/${token}`
      const tmpl = oteQuestionnaireEmail({ contact: name.trim(), link: url })
      window.open(`mailto:${email.trim()}?subject=${encodeURIComponent(tmpl.subject)}&body=${encodeURIComponent(tmpl.body)}`)
      setLink(url); setDone(true); setSending(false)
      return
    }
    const token = Math.random().toString(36).slice(2) + Date.now().toString(36)
    // Stamp the sender's initials (for the quote ref pm/<n>-<yy>/<initials>) AND their account id (created_by),
    // so the on-submit notification can be routed to THIS salesperson's login reliably — not by initials guessing.
    await supabase.from('enquiry_links').insert({ token, audit_type: auditType, recipient_email: email, recipient_name: name, initials: profile?.initials || null, created_by: profile?.id || null })
    const url = `${window.location.origin}/form/${token}`
    setLink(url)
    // Open mailto
    const tmpl = applyTemplate(getTemplate('enquiry'), {
      CLIENT_NAME: name || 'Sir/Madam',
      AUDIT_TYPE: auditType,
      FORM_LINK: url,
      FIRM_NAME: localStorage.getItem('firm_name') || 'CCIS Singapore Pte Ltd',
    })
    window.open(`mailto:${email}?subject=${encodeURIComponent(tmpl.subject)}&body=${encodeURIComponent(tmpl.body)}`)
    setDone(true)
    setSending(false)
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Send enquiry questionnaire</h2>
          <button onClick={onClose} style={{ border: 'none', background: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--text3)' }}>×</button>
        </div>
        {done ? (
          <div>
            <div className="notice notice-info">Email opened. Share this link if needed:</div>
            <div style={{ background: 'var(--bg2)', borderRadius: 'var(--radius)', padding: 12, fontSize: 13, wordBreak: 'break-all', marginBottom: 12 }}>
              <span style={{ color: 'var(--accent)' }}>{link}</span>
            </div>
            <button className="sm" onClick={() => navigator.clipboard.writeText(link)}>Copy link</button>
            <div className="modal-footer"><button className="primary" onClick={onClose}>Done</button></div>
          </div>
        ) : (
          <>
            <div className="field"><label>Audit type</label>
              <select value={auditType} onChange={e => setAuditType(e.target.value)}>
                {Object.values(AUDIT_TYPES).map(t => <option key={t} value={t}>{t}</option>)}
                <option value={OTE_TYPE}>OTE Endorsement (Overtime Exemption)</option>
              </select>
              {auditType === OTE_TYPE && <div style={{ fontSize: 13, color: 'var(--text3)', marginTop: 4 }}>For an existing OTE client (to pre-fill last year’s activities), use Quotations → OTE → Send OTE questionnaire.</div>}
            </div>
            <div className="field"><label>Client name (for email)</label>
              <input value={name} onChange={e => setName(e.target.value)} placeholder="Company or contact name" /></div>
            <div className="field"><label>Client email</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="contact@company.com" /></div>
            <div className="modal-footer">
              <button onClick={onClose}>Cancel</button>
              <button className="primary" onClick={send} disabled={sending}>Send questionnaire link</button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default function Dashboard() {
  const navigate = useNavigate()
  const [stats, setStats] = useState({ activeJobs: 0, wshcApplications: 0, pendingQuotes: 0, expiringSoon: 0, thisMonth: 0, newEnquiries: 0, officeReview: 0 })
  const [dash, setDash] = useState(null)   // operations model: { attention, groups, counts } from deriveDashboard
  const [loading, setLoading] = useState(true)
  const [showEnquiry, setShowEnquiry] = useState(false)

  const [auditorAlerts, setAuditorAlerts] = useState([])
  const [escalations, setEscalations] = useState([])
  const { profile, role, canApprove, logAction } = useAuth()

  useEffect(() => { loadAll(); loadAuditorAlerts(); loadEscalations() }, [])

  async function loadAuditorAlerts() {
    const { data: aus } = await supabase.from('auditors')
      .select('id,name,approval_status,wsh_auditor_expiry,wsho_status,wsho_licence_expiry,approved_scopes,audit_types')
      .eq('active', true)
    if (!aus) return
    const alerts = []
    const today = new Date()
    aus.forEach(a => {
      // Only alert on non-approved if the auditor has scopes set (actively being used). approved_scopes
      // is the single source — no fallback to the legacy audit_types column (which can be stale).
      const scopes = a.approved_scopes || []
      if (a.approval_status !== 'Approved' && scopes.length > 0) {
        alerts.push({ id: `appr-${a.id}`, name: a.name, msg: `Not approved — status: ${a.approval_status || 'Pending'}`, level: 'warn' })
      }
      if (a.wsh_auditor_expiry) {
        const days = Math.floor((new Date(a.wsh_auditor_expiry) - today) / 86400000)
        if (days < 0) alerts.push({ id: `wsh-exp-${a.id}`, name: a.name, msg: 'WSH Auditor approval EXPIRED', level: 'danger' })
        else if (days <= 30) alerts.push({ id: `wsh-30-${a.id}`, name: a.name, msg: `WSH Auditor approval expires in ${days} days`, level: 'danger' })
        else if (days <= 90) alerts.push({ id: `wsh-90-${a.id}`, name: a.name, msg: `WSH Auditor approval expires in ${days} days`, level: 'warn' })
      }
      if (a.wsho_status && a.wsho_licence_expiry) {
        const days = Math.floor((new Date(a.wsho_licence_expiry) - today) / 86400000)
        if (days < 0) alerts.push({ id: `wsho-exp-${a.id}`, name: a.name, msg: 'WSHO licence EXPIRED', level: 'danger' })
        else if (days <= 30) alerts.push({ id: `wsho-30-${a.id}`, name: a.name, msg: `WSHO licence expires in ${days} days`, level: 'danger' })
        else if (days <= 90) alerts.push({ id: `wsho-90-${a.id}`, name: a.name, msg: `WSHO licence expires in ${days} days`, level: 'warn' })
      }
    })
    setAuditorAlerts(alerts)
  }

  async function loadEscalations() {
    try {
      const { data, error } = await supabase.from('qm_escalations')
        .select('*, jobs(quote_ref, clients(name))')
        .eq('status', 'Pending')
        .order('created_at', { ascending: false })
      if (error) { console.warn('qm_escalations missing (run FIX_V19.sql):', error.message); return }
      setEscalations(data || [])
    } catch(e) { console.warn('loadEscalations error:', e) }
  }

  async function decideEscalation(esc, decision) {
    const remarks = decision === 'Rejected'
      ? window.prompt('Reason for rejection (required):')
      : window.prompt('Remarks (optional):')
    if (decision === 'Rejected' && !remarks) return
    await supabase.from('qm_escalations').update({
      status: decision,
      decided_by_name: profile?.name,
      decided_by_role: role,
      decided_at: new Date().toISOString(),
      decision_remarks: remarks || '',
    }).eq('id', esc.id)
    await logAction(
      `QM escalation ${decision.toLowerCase()} — ${esc.escalation_type.replace(/_/g, ' ')}`,
      'job', esc.job_id,
      esc.jobs?.clients?.name || esc.jobs?.quote_ref || esc.job_id,
      { auditor: esc.auditor_name, issue: esc.issue_detail, remarks: remarks || '', outcome: decision === 'Approved' ? 'PASS' : 'BLOCKED' }
    )
    // If approved — check if all escalations for this job are resolved, then auto-flag job for sending
    if (decision === 'Approved') {
      const { data: remaining } = await supabase.from('qm_escalations')
        .select('id').eq('job_id', esc.job_id).eq('status', 'Pending')
      if (!remaining || remaining.length === 0) {
        // All escalations resolved — update job to signal it can proceed
        await supabase.from('jobs').update({ qm_clearance: true }).eq('id', esc.job_id)
        await supabase.from('admin_alerts').insert({
          job_id: esc.job_id, type: 'qm_clearance',
          message: `All QM escalations resolved for this job. Audit plan can now be sent.`,
          resolved: false,
        })
      }
    }
    loadEscalations()
  }

  async function loadAll() {
    const today = new Date()
    const monthStart = new Date(today.getFullYear(), today.getMonth(), 1).toISOString()
    const monthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0).toISOString()

    // The Tracker-summary queries start NOW, alongside the rest (they used to wait for this first batch). Same queries;
    // awaited (and their errors caught) further down, exactly as before.
    const pipeQueries = Promise.all([
      // form_responses + prepared_by are REQUIRED so derivePipeline resolves expiry exactly like the
      // Tracker (resolveExpiry falls back to form_responses.expiry_date for quote-stage renewals).
      supabase.from('quotations').select('id,quote_ref,status,sent_date,signed_date,prospect_name,client_id,audit_type,prepared_by,created_at,form_responses'),
      supabase.from('renewal_reminders_sent').select('client_id,created_at'),
      supabase.from('pipeline_meta').select('*'),
    ])
    pipeQueries.catch(() => {})   // handled where it is awaited
    const [{ data: jobs }, { data: clients }, { data: quotes }, { data: coiResps }, { data: ackResps }, { data: coiDecs }] = await Promise.all([
      supabase.from('jobs').select(`${DASHBOARD_JOB_COLUMNS}, clients(name)`).order('created_at', { ascending: false }),
      supabase.from('clients').select('*'),
      supabase.from('quotations').select('id,status,sent_date,validity_days,audit_type,created_at,quote_ref,prospect_name,client_id,form_responses,clients(name)'),
      supabase.from('auditor_coi_responses').select('job_id,auditor_slot,auditor_name,risk_level'),
      supabase.from('client_ack_responses').select('job_id,answers,created_at'),
      supabase.from('coi_decisions').select('job_id,auditor_slot,auditor_name,decision'),
    ])

    const activeJobs = (jobs || []).filter(j => !['Completed','Cancelled'].includes(j.status)).length
    // Each UEN files its OWN WSHC application — a combined audit of N entities = N submissions, and each member
    // job already carries its own 'Pending WSHC Outcome' state. Count per-entity (like activeJobs), NOT the
    // rolled-up "one combined = one item" number.
    const wshcApplications = (jobs || []).filter(j => displayState(j) === 'Pending WSHC Outcome').length
    // KPI: office-review count uses displayState so approved-but-not-closed packages aren't mislabelled.
    const officeReviewJobs = (jobs || []).filter(j => displayState(j) === 'Pending Office Review')
    // KPI counts use the SAME predicates as the Quotations page lists (shared lib), on revision heads only.
    const toPrepareQuotes = quotesToPrepare(quotes || [])          // submitted enquiries → Draft, awaiting a quote
    const awaitingSign = quotesAwaitingSignature(quotes || [])     // Sent, not yet signed, not expired
    const pendingQuotes = awaitingSign.length
    const newEnquiries = toPrepareQuotes.length                   // "New enquiries" KPI = quotations to prepare
    const thisMonth = (jobs || []).filter(j => j.status !== 'Cancelled' && j.audit_date >= monthStart && j.audit_date <= monthEnd).length

    // (The old rough 6/9-month client internal audit watchlist was retired — client internal audit cycles now have
    //  their own reminders + the Tracker's Internal Audit view.)

    // Action Required — ONE outstanding issue per active job, by priority. Same logic as JobDetail:
    // ack valid = plan_signature === ack_signature; a COI counts only for the CURRENTLY-assigned auditor;
    // remarks come from the latest acknowledgement. Only jobs whose plan has been issued are considered.
    const latestAckByJob = new Map()
    for (const a of (ackResps || [])) {
      const prev = latestAckByJob.get(a.job_id)
      if (!prev || new Date(a.created_at) > new Date(prev.created_at)) latestAckByJob.set(a.job_id, a)
    }
    const coiRows = coiResps || []
    const actions = []     // blocking only: ack invalid OR COI missing
    const attention = []   // informational only: valid ack with client remarks
    for (const j of (jobs || [])) {
      if (['Completed', 'Cancelled'].includes(j.status)) continue
      // Client internal audit: no plan acknowledgement / COI exists — never flag it here (it would sit forever on
      // "Client acknowledgement pending").
      if (isOtherService(j.audit_type)) continue
      // Combined audit: the client acknowledgement + COI are done ONCE on the primary and represent the whole
      // group (the plan/COI now list every company). So a non-primary member never raises its OWN pre-audit
      // action item — the primary carries it for the group. (Post-submission office work is surfaced separately
      // by workflowState, per member, and is unaffected.)
      if (j.combined_group_id && !j.is_combined_primary) continue
      const base = { jobId: j.id, clientName: j.clients?.name || '—', auditDate: j.audit_date || null }
      // Unresolved high-risk COI blocks the audit at ANY stage (it even blocks sending), so surface it
      // BEFORE the "plan issued" gate — otherwise it would be hidden in the pre-send window.
      const highRisk = unresolvedHighRiskCoiNames(j, coiRows, coiDecs || [])
      if (highRisk.length) { actions.push({ ...base, issue: `High-risk COI unresolved (${highRisk.join(', ')})` }); continue }
      // Gate on the plan being SENT, not merely saved. The client_ack_token is minted on SAVE (so the ack link is
      // ready immediately), but "acknowledgement pending" must only surface once the plan has actually been SENT to
      // the client — otherwise a saved-but-unsent plan wrongly shows as awaiting the client. Mirrors JobDetail's
      // own "Audit plan sent" check (plan_sent_date || audit_plan_sent_at).
      if (!(j.plan_sent_date || j.audit_plan_sent_at)) continue
      const acknowledged = !!(j.ack_signature && j.plan_signature && j.ack_signature === j.plan_signature)
      if (!acknowledged) {
        actions.push({ ...base, issue: 'Client acknowledgement pending' })
        continue
      }
      const coiMissing = []
      if (j.auditor_id && !coiRows.some(c => c.job_id === j.id && c.auditor_slot === 'auditor1' && c.auditor_name === j.auditor_name)) coiMissing.push(j.auditor_name || 'Auditor 1')
      if (j.auditor2_id && !coiRows.some(c => c.job_id === j.id && c.auditor_slot === 'auditor2' && c.auditor_name === j.auditor2_name)) coiMissing.push(j.auditor2_name || 'Auditor 2')
      if (coiMissing.length) {
        actions.push({ ...base, issue: `COI pending (${coiMissing.join(', ')})` })
        continue
      }
      // valid acknowledgement + COI complete → client remarks are informational (Attention)
      const remark = (latestAckByJob.get(j.id)?.answers || {}).remarks
      const r = remark ? String(remark).trim() : ''
      if (r) attention.push({ ...base, remark: r.length > 140 ? r.slice(0, 140) + '…' : r })
    }
    // (actions/attention stay LOCAL — fed into deriveDashboard below, no separate state.)

    // bizSAFE pipeline rows — the SAME source the Tracker renders, so the "Expiring / overdue" KPI equals
    // the filtered Tracker list (a raw clients query previously diverged from the Tracker's business rules).
    let pipeRows = []
    try {
      const [{ data: quotFull }, { data: remSent }, { data: pmeta }] = await pipeQueries
      pipeRows = derivePipeline({ clients: clients || [], quotations: quotFull || [], jobs: jobs || [], reminders: remSent || [], meta: pmeta || [] })
    } catch (e) { console.error('[dashboard] derivePipeline failed', e) }

    // Expiring / overdue KPI = the SHARED predicate the Tracker filter uses → identical record set.
    const expiringRows = expiringRenewals(pipeRows)
    setStats({ activeJobs, wshcApplications, pendingQuotes, expiringSoon: expiringRows.length, thisMonth, newEnquiries, officeReview: officeReviewJobs.length })

    // Operations model — one pass over the already-loaded data: classify by responsibility + promote the
    // overdue/stale/at-risk items into the "Needs Attention Now" lane. Pure; no queries/mutations here.
    setDash(deriveDashboard({
      jobs: jobs || [], clients: clients || [], preAuditActions: actions, remarks: attention,
      pipeRows, toPrepare: toPrepareQuotes, awaitingSign, today,
    }))
    setLoading(false)
  }

  if (loading) return <div style={{ color: 'var(--text3)' }}>Loading…</div>

  // Calm re-grouping of the SAME deriveDashboard output — presentation only, no engine change.
  const view = dash ? buildView(dash, stats) : null

  return (
    <div>
      {showEnquiry && <SendEnquiryModal onClose={() => setShowEnquiry(false)} />}
      <div className="page-header">
        <h1>Dashboard</h1>
      {escalations.length > 0 && (
        <div className="card" style={{ marginBottom: 16, borderLeft: '3px solid var(--brand)' }}>
          <h2 className="card-title">
            Pending QM escalations
            <span style={{ marginLeft: 8, fontSize: 13, background: 'var(--danger-bg)', color: 'var(--danger)', padding: '2px 8px', borderRadius: 12 }}>{escalations.length}</span>
          </h2>
          {!canApprove && <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 10 }}>These require Quality Manager approval before the audit can proceed.</div>}
          {escalations.map(e => (
            <div key={e.id} style={{ padding: '12px 0', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'flex-start', gap: 12 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 500 }}>
                  {e.escalation_type === 'scope_mismatch' ? '⚠ Scope mismatch' : '⚠ COI medium risk'}
                  {' — '}{e.jobs?.clients?.name || e.jobs?.quote_ref || '—'}
                </div>
                <div style={{ fontSize: 13, color: 'var(--text2)', marginTop: 3 }}>{e.issue_detail}</div>
                <div style={{ fontSize: 13, color: 'var(--text2)', marginTop: 3 }}>
                  Raised by {e.raised_by_name} ({e.raised_by_role}) · {e.raised_at ? new Date(e.raised_at).toLocaleString('en-SG') : ''}
                </div>
              </div>
              {canApprove && (
                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  <button className="sm" style={{ background: '#EAF3DE', color: '#27500a', border: '1px solid #639922' }}
                    onClick={() => decideEscalation(e, 'Approved')}>Approve</button>
                  <button className="sm" style={{ background: '#FDECEA', color: '#791F1F', border: '1px solid #E24B4A' }}
                    onClick={() => decideEscalation(e, 'Rejected')}>Reject</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {auditorAlerts.length > 0 && (
        <div className="card" style={{ marginBottom: 16, borderLeft: '3px solid var(--danger)' }}>
          <h2 className="card-title">Auditor compliance alerts</h2>
          {auditorAlerts.map(a => (
            <div key={a.id} style={{ display: 'flex', gap: 10, fontSize: 13, padding: '6px 0', borderBottom: '1px solid var(--border)', alignItems: 'center' }}>
              <span style={{ color: a.level === 'danger' ? 'var(--danger)' : 'var(--warn)', flexShrink: 0 }}>{a.level === 'danger' ? '⛔' : '⚠️'}</span>
              <span><strong>{a.name}</strong> — {a.msg}</span>
            </div>
          ))}
        </div>
      )}
        <button className="primary" onClick={() => setShowEnquiry(true)}>+ Send enquiry form</button>
      </div>

      {/* ── CALM OPS VIEW — summary · your desk · renewals at risk · in-motion counts. Same data, re-grouped. ── */}
      {view && (
        <>
          {/* One-line summary — the whole dashboard in a sentence. */}
          <div className="card" style={{ marginBottom: 16, padding: '15px 18px', borderLeft: '4px solid var(--brand)', fontSize: 14, lineHeight: 1.55, color: 'var(--text)' }}>
            {view.summary}
          </div>

          {/* Your desk — human-only actions (no agent behind them). */}
          <div className="card" style={{ marginBottom: 16, padding: '13px 16px' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 8 }}>
              <h2 style={{ fontSize: 13, letterSpacing: '0.1em', textTransform: 'uppercase', fontWeight: 800, color: 'var(--brand)', margin: 0 }}>Your desk</h2>
              <span style={{ fontSize: 13, fontWeight: 700, color: '#fff', background: 'var(--brand)', borderRadius: 10, padding: '1px 9px' }}>{view.desk.length}</span>
              <span style={{ fontSize: 13, color: 'var(--text3)', marginLeft: 'auto' }}>only things a person has to do</span>
            </div>
            {view.desk.map(it => (
              <div key={it.key} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 0', borderTop: '1px solid var(--border)' }}>
                <span style={{ fontSize: 13, fontWeight: 600, flex: '0 1 auto', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={it.client}>{it.client}</span>
                <span style={{ fontSize: 13, color: 'var(--text2)', flex: '1 1 auto', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.label}{it.reason ? <> · <span style={{ color: it.tier === 'overdue' ? 'var(--danger)' : 'var(--warn)', fontWeight: 500 }}>{it.reason}</span></> : (it.meta ? ` · ${it.meta}` : '')}</span>
                <button className="sm" style={{ flexShrink: 0 }} onClick={() => navigate(it.nav)}>{actionLabel(it.nav)}</button>
              </div>
            ))}
            {view.deskTicks.map((t, i) => (
              <div key={'tick' + i} style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '8px 0', borderTop: '1px solid var(--border)', fontSize: 13, color: 'var(--text3)' }}>
                <span style={{ color: '#2e8b3d', fontWeight: 800 }}>✓</span> {t}
              </div>
            ))}
          </div>

          {/* Renewals at risk — cert expiring & not secured. */}
          {view.atRisk.length > 0 && (
            <div className="card" style={{ marginBottom: 16, padding: '13px 16px' }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 8 }}>
                <h2 style={{ fontSize: 13, letterSpacing: '0.1em', textTransform: 'uppercase', fontWeight: 800, color: 'var(--danger)', margin: 0 }}>Renewals at risk</h2>
                <span style={{ fontSize: 13, fontWeight: 700, color: '#fff', background: 'var(--danger)', borderRadius: 10, padding: '1px 9px' }}>{view.atRisk.length}</span>
                <span style={{ fontSize: 13, color: 'var(--text3)', marginLeft: 'auto' }}>cert expiring &amp; not secured — a call may save it</span>
              </div>
              {view.atRisk.map(it => (
                <div key={it.key} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 0', borderTop: '1px solid var(--border)' }}>
                  <span style={{ flexShrink: 0, fontSize: 13 }}>{it.tier === 'overdue' ? '🔴' : '🟠'}</span>
                  <span style={{ fontSize: 13, fontWeight: 700, flex: '0 1 auto', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={it.client}>{it.client}</span>
                  <span style={{ fontSize: 13, color: 'var(--danger)', fontWeight: 500, flex: '1 1 auto', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={it.reason}>{it.reason}</span>
                  <button className="sm" style={{ flexShrink: 0 }} onClick={() => navigate(it.nav)}>{actionLabel(it.nav)}</button>
                </div>
              ))}
            </div>
          )}

          {/* To schedule — booked jobs to book in, limited to within ~2 months of expiry (buffer vs the WSHC window). */}
          {view.toSchedule.length > 0 && (
            <div className="card" style={{ marginBottom: 16, padding: '13px 16px' }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 8 }}>
                <h2 style={{ fontSize: 13, letterSpacing: '0.1em', textTransform: 'uppercase', fontWeight: 800, color: 'var(--text2)', margin: 0 }}>To schedule</h2>
                <span style={{ fontSize: 13, fontWeight: 700, color: '#fff', background: 'var(--text2)', borderRadius: 10, padding: '1px 9px' }}>{view.toSchedule.length}</span>
                <span style={{ fontSize: 13, color: 'var(--text3)', marginLeft: 'auto' }}>book the audit — within 2 months of expiry</span>
              </div>
              {view.toSchedule.map(it => (
                <div key={it.key} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 0', borderTop: '1px solid var(--border)' }}>
                  <span style={{ fontSize: 13, fontWeight: 600, flex: '0 1 auto', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={it.client}>{it.client}</span>
                  <span style={{ fontSize: 13, color: 'var(--text2)', flex: '1 1 auto', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.meta || 'Schedule audit date'}</span>
                  <button className="sm" style={{ flexShrink: 0 }} onClick={() => navigate(it.nav)}>{actionLabel(it.nav)}</button>
                </div>
              ))}
            </div>
          )}

          {/* In motion — counts only; the agents handle these. Click a tile to open the full list. */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(220px, 100%), 1fr))', gap: 14, marginBottom: 20 }}>
            {view.motion.map((m, i) => (
              <div key={'m' + i} className="card" style={{ margin: 0, padding: '15px 16px', cursor: 'pointer', position: 'relative' }} onClick={() => navigate(m.nav)}>
                <span style={{ position: 'absolute', top: 12, right: 12, fontSize: 13, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: '#3f7d5a', background: '#e7f4ec', padding: '2px 8px', borderRadius: 10 }}>{m.tag}</span>
                <div style={{ fontSize: 26, fontWeight: 750, letterSpacing: '-1px' }}>{m.n}</div>
                <div style={{ fontSize: 13, color: 'var(--text2)', marginTop: 2, lineHeight: 1.4 }}>{m.label}</div>
                <div style={{ fontSize: 13, color: 'var(--text3)', marginTop: 8 }}>{m.by}</div>
              </div>
            ))}
          </div>

          {/* Drill-down lives behind the tiles above — each click-throughs to its full list — so the dashboard
              itself stays calm. The old Waiting/Monitoring/Recent queues were intentionally removed. */}
        </>
      )}
    </div>
  )
}

// Dashboard actions ONLY navigate (MVP: no action-in-place). Label reflects the destination.
function actionLabel(nav) {
  if (nav.startsWith('/quotations')) return 'Open quotations'
  if (nav.startsWith('/pipeline')) return 'Open tracker'
  if (nav.startsWith('/clients')) return 'View'
  return 'View job'
}

// PRESENTATION ONLY — re-groups the exact deriveDashboard output into the calm view. No queries, no mutations,
// no change to the classification engine. Attention items already carry {group, reason, tier}; non-promoted
// office actions live in groups.needsAction. We route by whose court it's in:
//   • Your desk    = office/auditor actions a HUMAN must do (needsAction + attention groups A/C).
//   • At risk      = cert expiring & renewal not secured (attention group E, or a group-B quote w/ a cert reason).
//   • In motion    = agent-handled — counts only, never red.
function buildView(dash, stats) {
  const A = dash.attention || []
  const certRisk = it => /cert lapsed|cert expires|expires |expired |overdue/i.test(it.reason || '')
  // Your desk = TIME-SENSITIVE human actions. Two gates keep it calm & aligned with the 2-month WSHC window
  // (sortVal on these items = days-until-cert-expiry, or a 9999 sentinel when there is no cert = new application):
  //   • "Schedule audit date" → its own list, showing only RENEWALS within ~2 months of expiry (buffer so we
  //     don't book too early) + new applications (no deadline). Far-out renewals stay hidden until they're near.
  //   • "Record WSHC submission" → a still-locked renewal (>2 months out) is hidden until its window opens; it
  //     would otherwise sit on the desk for weeks. New apps & in-window renewals show normally.
  const needs = dash.groups.needsAction || []
  const REAL = 9000                                               // sortVal ≥ REAL ⇒ no cert expiry (new application)
  const within2mo = it => it.sortVal != null && it.sortVal <= 62  // days-to-expiry within ~2 calendar months (incl. overdue)
  const noExpiry = it => it.sortVal == null || it.sortVal >= REAL
  const toSchedule = needs.filter(it => it.label === 'Schedule audit date' && (within2mo(it) || noExpiry(it)))
  const deskCore = needs.filter(it => it.label !== 'Schedule audit date'
    && !(it.label === 'Record WSHC submission' && !within2mo(it) && !noExpiry(it)))
  const desk = [...A.filter(it => it.group === 'A' || it.group === 'C'), ...deskCore]
  // At risk = renewal with NOTHING secured yet: nothing scheduled (group E), or an unsigned QUOTE (not a
  // done-audit awaiting signature) whose cert is expiring. A signed/acknowledged job is already secured.
  const atRisk = A.filter(it => it.group === 'E' || (it.group === 'B' && certRisk(it) && String(it.nav || '').startsWith('/quotations')))
  const deskTicks = []
  if (!desk.some(it => /review/i.test(it.label || ''))) deskTicks.push('No reports waiting for your review')
  if (stats.newEnquiries === 0 && !desk.some(it => /prepare/i.test(it.label || ''))) deskTicks.push('No new enquiries to quote')
  // Per-UEN WSHC applications (combined members counted individually) — falls back to the rolled-up count.
  const wshc = stats.wshcApplications ?? ((dash.counts.waitingWSHC && dash.counts.waitingWSHC.total) || 0)
  const motion = [
    { n: stats.pendingQuotes, label: 'Quotes out, awaiting client signature', by: 'Chaser nudges at day 4 · 14 · 26', tag: 'auto', nav: '/quotations?filter=awaiting' },
    { n: wshc, label: 'Pending WSHC Outcome', by: 'Check WSHC once a week', tag: 'auto', nav: '/jobs?status=' + encodeURIComponent('Pending WSHC Outcome') },
    { n: stats.activeJobs, label: 'Active audit jobs in progress', by: 'Client acknowledgements auto-chased', tag: 'live', nav: '/jobs' },
  ]
  const deskN = desk.length, riskN = atRisk.length
  const deskPhrase = deskN === 0 ? 'Nothing needs your hands today' : `${deskN} thing${deskN === 1 ? '' : 's'} need${deskN === 1 ? 's' : ''} you today`
  const riskPhrase = riskN > 0 ? `, and ${riskN} renewal${riskN === 1 ? '' : 's'} need${riskN === 1 ? 's' : ''} attention this week` : ''
  const summary = `${deskPhrase}${riskPhrase}. Everything else — ${stats.pendingQuotes} quotes out, ${wshc} at WSHC, ${stats.activeJobs} active jobs — is running on its own.`
  return { desk, deskTicks, atRisk, toSchedule, motion, summary }
}

// (The old GroupSection responsibility-list component was removed with the detailed queues — the calm
//  Your desk / Renewals at risk / To schedule cards render their own rows now.)
